import React, {useState} from 'react';
import {Button, Input, Modal, Space, Table, Popconfirm} from 'antd';
import {DeleteOutlined, PlusOutlined, SettingOutlined} from '@ant-design/icons';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import GoToUserInfoButton from '../../shared/components/buttons/GoToUserInfoButton';
import {
    useMonitoringIpLog,
    useMonitoringIp,
    useSaveMonitoringIp,
    useDeleteMonitoringIpLog,
    useDeleteMonitoringIp
} from './useHackTable';

const IpMonitoring = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newIp, setNewIp] = useState('');

    const {data: logData, isLoading: logLoading, refetch: refetchLog} = useMonitoringIpLog();
    const {data: ipList, isLoading: ipLoading, refetch: refetchIp} = useMonitoringIp();
    const {mutateAsync: saveIp, isPending: savingIp} = useSaveMonitoringIp();
    const {mutateAsync: deleteLog, isPending: deletingLog} = useDeleteMonitoringIpLog();
    const {mutateAsync: deleteIp, isPending: deletingIp} = useDeleteMonitoringIp();

    const handleDeleteLog = async (pid) => {
        try {
            const res = await deleteLog(pid);
            toast.success(res.data.message || '삭제 완료');
            refetchLog();
        } catch (err) {
            console.error('로그 삭제 실패:', err);
            toast.error('로그 삭제 실패');
        }
    };

    const handleAddIp = async () => {
        if (!newIp.trim()) {
            toast.error('IP를 입력해주세요');
            return;
        }

        try {
            const res = await saveIp(newIp.trim());
            toast.success(res.data.message || 'IP 추가 완료');
            setNewIp('');
            refetchIp();
        } catch (err) {
            console.error('IP 추가 실패:', err);
            toast.error('IP 추가 실패');
        }
    };

    const handleDeleteIp = async (ip) => {
        try {
            const res = await deleteIp(ip);
            toast.success(res.data.message || 'IP 삭제 완료');
            refetchIp();
        } catch (err) {
            console.error('IP 삭제 실패:', err);
            toast.error('IP 삭제 실패');
        }
    };

    const columns = [
        {
            title: 'PID',
            dataIndex: 'pid',
            key: 'pid',
            width: 300,
        },
        {
            title: '접속 횟수',
            key: 'count',
            width: 120,
            render: (_, record) => `${record.logs.length}회`,
        },
        {
            title: '작업',
            key: 'action',
            width: 200,
            render: (_, record) => (
                <Space>
                    <GoToUserInfoButton pid={record.pid}/>
                    <Popconfirm
                        title="정말 삭제하시겠습니까?"
                        onConfirm={() => handleDeleteLog(record.pid)}
                        okText="삭제"
                        cancelText="취소"
                    >
                        <Button danger icon={<DeleteOutlined/>} loading={deletingLog}>
                            삭제
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const expandColumns = [
        {
            title: 'IP',
            dataIndex: 'ip',
            key: 'ip',
            width: 300,
        },
        {
            title: '접속일시',
            dataIndex: 'created',
            key: 'created',
            width: 180,
            render: (created) => dayjs(created).format('YYYY-MM-DD HH:mm:ss'),
            sorter: (a, b) => dayjs(a.created).unix() - dayjs(b.created).unix(),
            defaultSortOrder: 'descend',
        },
    ];

    const expandedRowRender = (record) => (
        <Table
            columns={expandColumns}
            dataSource={record.logs.map((log, idx) => ({...log, key: idx}))}
            pagination={false}
            size="small"
        />
    );

    const ipColumns = [
        {
            title: 'IP 주소',
            dataIndex: 'ip',
            key: 'ip',
        },
        {
            title: '작업',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Popconfirm
                    title="이 IP를 삭제하시겠습니까?"
                    onConfirm={() => handleDeleteIp(record.ip)}
                    okText="삭제"
                    cancelText="취소"
                >
                    <Button danger icon={<DeleteOutlined/>} size="small" loading={deletingIp}>
                        삭제
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    const ipDataSource = (ipList || []).map((ip, idx) => ({key: idx, ip}));

    const groupedLogData = Object.values(
        (logData?.rows || []).reduce((acc, row) => {
            if (!acc[row.pid]) {
                acc[row.pid] = {pid: row.pid, key: row.pid, logs: []};
            }
            acc[row.pid].logs.push(row);
            return acc;
        }, {})
    );

    return (
        <div style={{padding: '20px'}}>
            <div style={{marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h2 style={{margin: 0}}>IP 모니터링 로그</h2>
                <Button
                    type="primary"
                    icon={<SettingOutlined/>}
                    onClick={() => setIsModalOpen(true)}
                >
                    IP 목록 관리
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={groupedLogData}
                loading={logLoading}
                expandable={{expandedRowRender}}
                pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `총 ${total}건`,
                }}
            />

            <Modal
                title="모니터링 IP 목록 관리"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={500}
            >
                <div style={{marginBottom: '16px'}}>
                    <Space.Compact style={{width: '100%'}}>
                        <Input
                            placeholder="추가할 IP 주소 입력"
                            value={newIp}
                            onChange={(e) => setNewIp(e.target.value)}
                            onPressEnter={handleAddIp}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={handleAddIp}
                            loading={savingIp}
                        >
                            추가
                        </Button>
                    </Space.Compact>
                </div>

                <Table
                    columns={ipColumns}
                    dataSource={ipDataSource}
                    loading={ipLoading}
                    pagination={false}
                    size="small"
                    locale={{emptyText: '등록된 IP가 없습니다'}}
                />
            </Modal>
        </div>
    );
};

export default IpMonitoring;
